import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  TextFieldProps,
  useTheme,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { debounce } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { WbDivider } from '../WbDivider';

const useStyles = makeStyles(
  theme => ({
    root: {
      backgroundColor: theme.palette.white,
      height: 'auto',
    },
    srOnly: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  }),
  { name: 'WbSearch' },
);

export type WbSearchProps = Omit<TextFieldProps, 'onChange'> & {
  debounced?: boolean;
  onChange?: (value: string) => void;
  submit?: () => void;
  showEnter?: boolean;
  inputHeight?: string | number;
  inputClassName?: string;
  hasAutocomplete?: boolean;
  hideStartAdornment?: boolean;
};

export const WbSearch = ({
  onChange,
  value,
  debounced,
  placeholder,
  submit,
  showEnter,
  inputHeight,
  inputClassName,
  hasAutocomplete = false,
  hideStartAdornment = false,
  ...props
}: WbSearchProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const [search, setSearch] = useState(value ?? '');
  const hintId = useId();

  const inputPropsWithStyle = !showEnter
    ? props.InputProps
    : {
        ...props.InputProps,
        style: { borderRadius: '0px 4px 4px 0px', ...props.InputProps?.style },
      };

  // Workaround to clear input when user clear all filters
  useEffect(() => {
    if (value === '' || value === undefined) setSearch('');
  }, [value]);

  const handleDebounceFn = useCallback(
    (v: string) => {
      onChange?.(v);
    },
    [onChange],
  );

  const debounceFn = useMemo(
    () => debounce(handleDebounceFn, 500),
    [handleDebounceFn],
  );

  const handleChangeSearch = (e: any) => {
    const newValue = e.currentTarget.value;
    setSearch(newValue);
    if (debounced) debounceFn(newValue);
    else handleDebounceFn(newValue);
  };

  const handleClearSearch = () => {
    onChange?.('');
    setSearch('');
  };

  const enterButton = (
    <IconButton
      aria-label="search"
      onClick={submit}
      style={{ color: theme.palette.primary.main }}
    >
      <SearchIcon fontSize="small" />
    </IconButton>
  );

  const clearButtonVisible = Boolean(search);
  const searchId = useId();

  return (
    <>
      {hasAutocomplete && (
        <span id={hintId} className={classes.srOnly}>
          Search suggestions will appear below as you type
        </span>
      )}
      <TextField
        fullWidth
        variant="outlined"
        id={searchId}
        size="small"
        classes={{ root: classes.root }}
        value={search ?? ''}
        onChange={handleChangeSearch}
        placeholder={placeholder ?? 'Search'}
        {...props}
        InputProps={{
          ...inputPropsWithStyle,
          className: inputClassName,
          startAdornment: hideStartAdornment ? undefined : (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                style={{
                  visibility: clearButtonVisible ? undefined : 'hidden',
                }}
                aria-label="clear"
                onClick={handleClearSearch}
                disabled={!search}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>

              {showEnter && (
                <>
                  {clearButtonVisible && (
                    <WbDivider
                      orientation="vertical"
                      style={{ margin: '8px' }}
                    />
                  )}
                  {enterButton}
                </>
              )}
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...props.inputProps,
          'aria-label': placeholder ?? 'Search',
          'aria-describedby': hasAutocomplete ? hintId : undefined,
        }}
      />
    </>
  );
};
